import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Label,
  MenuToggle,
  type MenuToggleElement,
  Pagination,
  SearchInput,
  Select,
  SelectList,
  SelectOption,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
  Tooltip,
} from '@patternfly/react-core';
import { ActionsColumn, type IAction, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { ExclamationCircleIcon, SearchIcon, UserTagIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import type { BackupVmMapping } from '../../../types/models/BackupVmMapping';
import type { BackupVmMappingFilters } from '../../../hooks/useBackupVmMappings';
import { formatWallClockDateTime } from '../../../utils/dateFormat';

const SUBTLE_TEXT = { color: 'var(--pf-t--global--text--color--subtle)' };
const CLIENT_ALL = 'all';

export interface ClientFilterOption {
  id: number;
  name: string;
}

interface BackupOverridesTabProps {
  mappings: BackupVmMapping[];
  loading: boolean;
  error: string | null;
  filters: BackupVmMappingFilters;
  onFiltersChange: (next: BackupVmMappingFilters) => void;
  /** Every client seen to hold an override so far — kept by the page so it survives filtering. */
  clientOptions: ClientFilterOption[];
  onRetry: () => void;
  onEdit: (mapping: BackupVmMapping) => void;
  onRemove: (mapping: BackupVmMapping) => void;
}

/**
 * The decisions already made: one row per deliberate per-VM attribution, across all tenants.
 *
 * The `Overrides` column is the point of the table — it names the client the row *replaced*, so a
 * staff member can see at a glance which VMs have been pulled out of a shared house directory.
 *
 * A row whose `subject_exists` is false matched no reported backup. That is not necessarily a
 * mistake (an override may legitimately be pre-seeded before a VM's first report), which is why it
 * is flagged rather than treated as an error.
 */
function BackupOverridesTab({
  mappings,
  loading,
  error,
  filters,
  onFiltersChange,
  clientOptions,
  onRetry,
  onEdit,
  onRemove,
}: BackupOverridesTabProps) {
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isClientOpen, setIsClientOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchValue);
      setPage(1);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchValue]);

  useEffect(() => {
    setPage(1);
  }, [filters.clientID, filters.orphansOnly, filters.backupMappingId]);

  const filteredMappings = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (q === '') {
      return mappings;
    }
    return mappings.filter(
      (mapping) =>
        mapping.vm_name.toLowerCase().includes(q) ||
        mapping.hypervisor.toLowerCase().includes(q) ||
        (mapping.client_name ?? '').toLowerCase().includes(q) ||
        (mapping.directory_client_name ?? '').toLowerCase().includes(q) ||
        (mapping.customer_directory ?? '').toLowerCase().includes(q) ||
        (mapping.backup_target ?? '').toLowerCase().includes(q),
    );
  }, [mappings, debouncedSearch]);

  const paginatedMappings = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredMappings.slice(start, start + perPage);
  }, [filteredMappings, page, perPage]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredMappings.length / perPage));
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredMappings.length, perPage, page]);

  const onSetPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, pageNumber: number) => {
    setPage(pageNumber);
  };

  const onPerPageSelect = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const clearSearch = () => {
    setSearchValue('');
    setDebouncedSearch('');
    setPage(1);
  };

  const clientFilterLabel =
    filters.clientID === null
      ? 'All clients'
      : (clientOptions.find((option) => option.id === filters.clientID)?.name ?? `Client #${filters.clientID}`);

  const rowActions = (mapping: BackupVmMapping): IAction[] => [
    { title: 'Edit', onClick: () => onEdit(mapping) },
    { isSeparator: true },
    { title: 'Remove', isDanger: true, onClick: () => onRemove(mapping) },
  ];

  const directoryOf = (mapping: BackupVmMapping): string => {
    if (mapping.backup_target === null && mapping.customer_directory === null) {
      return `Directory #${mapping.backup_mapping_id}`;
    }
    return `${mapping.backup_target ?? '—'} / ${mapping.customer_directory ?? '—'}`;
  };

  if (loading) {
    return <CenteredSpinner />;
  }

  if (error !== null) {
    return (
      <EmptyState
        titleText="Unable to load attributions"
        icon={ExclamationCircleIcon}
        variant="lg"
        headingLevel="h2"
        status="danger"
      >
        <EmptyStateBody>{error}</EmptyStateBody>
        <EmptyStateFooter>
          <EmptyStateActions>
            <Button variant="primary" onClick={onRetry}>
              Retry
            </Button>
          </EmptyStateActions>
        </EmptyStateFooter>
      </EmptyState>
    );
  }

  const renderBody = () => {
    if (mappings.length === 0) {
      return filters.orphansOnly || filters.clientID !== null || filters.backupMappingId !== null ? (
        <EmptyState titleText="No attributions match these filters" icon={SearchIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>Widen the filters to see the attributions recorded for other clients.</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button
                variant="primary"
                onClick={() => onFiltersChange({ backupMappingId: null, clientID: null, orphansOnly: false })}
              >
                Clear filters
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      ) : (
        <EmptyState titleText="No per-VM attributions yet" icon={UserTagIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            Every backed-up VM is attributed by the client its backup directory maps to. Attribute a VM from the
            review queue to override that for one machine.
          </EmptyStateBody>
        </EmptyState>
      );
    }

    if (filteredMappings.length === 0) {
      return (
        <EmptyState titleText="No attributions match your search" icon={SearchIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            Try a different VM name, hypervisor, directory or client name, or clear the filter.
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={clearSearch}>
                Clear filter
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    return (
      <>
        <Table aria-label="Per-VM backup attributions" variant="compact">
          <Thead>
            <Tr>
              <Th>Virtual machine</Th>
              <Th>Directory</Th>
              <Th>Attributed to</Th>
              <Th>Overrides</Th>
              <Th>Note</Th>
              <Th>Assigned</Th>
              <Th screenReaderText="Row actions" />
            </Tr>
          </Thead>
          <Tbody>
            {paginatedMappings.map((mapping) => (
              <Tr key={mapping.id}>
                <Td dataLabel="Virtual machine">
                  <strong>{mapping.vm_name}</strong>
                  {!mapping.subject_exists && (
                    <>
                      {' '}
                      {/* The visible "Unmatched" text carries the meaning; the tooltip only adds
                          detail, so the flag is not lost on assistive technologies. */}
                      <Tooltip content="No backup matches this VM — it may be a typo, a pre-seeded VM not yet reported, or a directory that was removed.">
                        <Label status="warning" isCompact>
                          Unmatched
                        </Label>
                      </Tooltip>
                    </>
                  )}
                  <div style={SUBTLE_TEXT}>{mapping.hypervisor}</div>
                </Td>
                <Td dataLabel="Directory">{directoryOf(mapping)}</Td>
                <Td dataLabel="Attributed to">{mapping.client_name ?? `Client #${mapping.clientID}`}</Td>
                <Td dataLabel="Overrides">
                  {mapping.directory_client_name ?? (
                    <span style={SUBTLE_TEXT}>
                      {mapping.directory_clientID === null ? 'Unattributed directory' : `Client #${mapping.directory_clientID}`}
                    </span>
                  )}
                </Td>
                <Td dataLabel="Note">{mapping.note ?? <span style={SUBTLE_TEXT}>—</span>}</Td>
                <Td dataLabel="Assigned">{formatWallClockDateTime(mapping.assigned_at)}</Td>
                <Td isActionCell>
                  <ActionsColumn items={rowActions(mapping)} />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>

        <Pagination
          itemCount={filteredMappings.length}
          perPage={perPage}
          page={page}
          onSetPage={onSetPage}
          onPerPageSelect={onPerPageSelect}
          variant="bottom"
        />
      </>
    );
  };

  return (
    <>
      <Toolbar>
        <ToolbarContent>
          <ToolbarItem>
            <SearchInput
              placeholder="Search by VM, hypervisor, directory or client..."
              value={searchValue}
              onChange={(_event, value) => setSearchValue(value)}
              onClear={clearSearch}
            />
          </ToolbarItem>
          <ToolbarItem>
            <Select
              id="overrides-client-filter"
              isOpen={isClientOpen}
              selected={filters.clientID === null ? CLIENT_ALL : filters.clientID}
              onSelect={(_event, value) => {
                onFiltersChange({ ...filters, clientID: value === CLIENT_ALL ? null : Number(value) });
                setIsClientOpen(false);
              }}
              onOpenChange={(open) => setIsClientOpen(open)}
              toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                <MenuToggle
                  ref={toggleRef}
                  onClick={() => setIsClientOpen((open) => !open)}
                  isExpanded={isClientOpen}
                  aria-label="Filter by attributed client"
                >
                  {clientFilterLabel}
                </MenuToggle>
              )}
              shouldFocusToggleOnSelect
            >
              <SelectList>
                <SelectOption value={CLIENT_ALL}>All clients</SelectOption>
                {clientOptions.map((option) => (
                  <SelectOption key={option.id} value={option.id}>
                    {option.name}
                  </SelectOption>
                ))}
              </SelectList>
            </Select>
          </ToolbarItem>
          <ToolbarItem>
            <Checkbox
              id="overrides-unmatched-only"
              label="Unmatched only"
              isChecked={filters.orphansOnly}
              onChange={(_event, checked) => onFiltersChange({ ...filters, orphansOnly: checked })}
            />
          </ToolbarItem>
          <ToolbarItem variant="pagination">
            <Pagination
              itemCount={filteredMappings.length}
              perPage={perPage}
              page={page}
              onSetPage={onSetPage}
              onPerPageSelect={onPerPageSelect}
            />
          </ToolbarItem>
        </ToolbarContent>
      </Toolbar>

      {renderBody()}
    </>
  );
}

export default BackupOverridesTab;
